import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiUpload,
  FiCheck,
  FiChevronRight,
  FiChevronLeft,
  FiAlertCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { admissionAPI } from "../services/api";

const STEPS = [
  "Student Information",
  "Parent/Guardian Info",
  "Documents & Submit",
];

const initialForm = {
  // Step 1
  studentName: "",
  dateOfBirth: "",
  gender: "",
  classApplyingFor: "",
  previousSchool: "",
  nationality: "Nepali",
  // Step 2
  parentName: "",
  relationship: "",
  email: "",
  phone: "",
  alternatePhone: "",
  address: "",
  city: "",
  occupation: "",
  // Step 3
  documents: null,
  notes: "",
};

const classOptions = [
  "Nursery",
  "KG",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
];

const AdmissionForm = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 0) {
      if (!form.studentName.trim())
        newErrors.studentName = "Student name is required";
      if (!form.dateOfBirth)
        newErrors.dateOfBirth = "Date of birth is required";
      if (!form.gender) newErrors.gender = "Gender is required";
      if (!form.classApplyingFor)
        newErrors.classApplyingFor = "Class is required";
    } else if (step === 1) {
      if (!form.parentName.trim())
        newErrors.parentName = "Parent name is required";
      if (!form.email.trim()) newErrors.email = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        newErrors.email = "Invalid email address";
      if (!form.phone.trim()) newErrors.phone = "Phone number is required";
      if (!form.address.trim()) newErrors.address = "Address is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (val !== null && val !== undefined) formData.append(key, val);
      });
      const response = await admissionAPI.create(formData);
      const id =
        response.data?.data?.admission?._id ||
        `GA-${Date.now().toString().slice(-6)}`;
      setApplicationId(id);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to submit application. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const ErrorMsg = ({ name }) =>
    errors[name] ? (
      <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
        <FiAlertCircle className="w-3 h-3" />
        {errors[name]}
      </div>
    ) : null;

  const inputClass = (name) =>
    `w-full px-4 py-3 border ${errors[name] ? "border-red-400 bg-red-50" : "border-gray-200"} rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-colors`;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, type: "spring" }}
          className="bg-white rounded-3xl p-10 max-w-lg w-full text-center shadow-xl border border-gray-100"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiCheck className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-3xl font-serif font-bold text-primary-500 mb-3">
            Application Submitted!
          </h2>
          <p className="text-gray-600 mb-4">
            Thank you for applying to Gyanpunja English Boarding Secondary School. We have
            received your application and will contact you
            within 3-5 business days.
          </p>
          <div className="bg-primary-50 border border-primary-100 rounded-xl px-6 py-4 mb-6">
            <p className="text-primary-500 text-sm font-medium">
              Application Reference ID
            </p>
            <p className="text-primary-700 text-2xl font-bold mt-1">
              {applicationId}
            </p>
          </div>
          <p className="text-gray-500 text-sm mb-6">
            Please save this reference ID. You will need it to track your
            application status.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Back to Home
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="hero-gradient py-16 text-center">
        <h1 className="text-4xl font-serif font-bold text-white mb-2">
          Online Admission Form
        </h1>
        <p className="text-white/70">
          Academic Year 2025-2026 | Gyanpunja English Boarding Secondary School
        </p>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((label, idx) => (
              <div key={idx} className="flex items-center gap-2 flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    idx < step
                      ? "bg-green-500 text-white"
                      : idx === step
                        ? "bg-primary-500 text-white"
                        : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {idx < step ? <FiCheck className="w-4 h-4" /> : idx + 1}
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 rounded-full ${idx < step ? "bg-green-500" : "bg-gray-200"}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {STEPS.map((label, idx) => (
              <span
                key={idx}
                className={`text-xs font-medium ${idx === step ? "text-primary-500" : "text-gray-400"}`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-primary-500 font-serif mb-6">
                  Student Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      name="studentName"
                      value={form.studentName}
                      onChange={handleChange}
                      placeholder="Enter student's full name"
                      className={inputClass("studentName")}
                    />
                    <ErrorMsg name="studentName" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={form.dateOfBirth}
                        onChange={handleChange}
                        className={inputClass("dateOfBirth")}
                      />
                      <ErrorMsg name="dateOfBirth" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender *
                      </label>
                      <select
                        name="gender"
                        value={form.gender}
                        onChange={handleChange}
                        className={inputClass("gender")}
                      >
                        <option value="">Select gender</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                      </select>
                      <ErrorMsg name="gender" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Class Applying For *
                    </label>
                    <select
                      name="classApplyingFor"
                      value={form.classApplyingFor}
                      onChange={handleChange}
                      className={inputClass("classApplyingFor")}
                    >
                      <option value="">Select class</option>
                      {classOptions.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                    <ErrorMsg name="classApplyingFor" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Previous School
                    </label>
                    <input
                      name="previousSchool"
                      value={form.previousSchool}
                      onChange={handleChange}
                      placeholder="Name of previous school (if any)"
                      className={inputClass("previousSchool")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nationality
                    </label>
                    <input
                      name="nationality"
                      value={form.nationality}
                      onChange={handleChange}
                      className={inputClass("nationality")}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-primary-500 font-serif mb-6">
                  Parent/Guardian Information
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        name="parentName"
                        value={form.parentName}
                        onChange={handleChange}
                        placeholder="Parent's full name"
                        className={inputClass("parentName")}
                      />
                      <ErrorMsg name="parentName" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Relationship
                      </label>
                      <select
                        name="relationship"
                        value={form.relationship}
                        onChange={handleChange}
                        className={inputClass("relationship")}
                      >
                        <option value="">Select</option>
                        <option>Father</option>
                        <option>Mother</option>
                        <option>Guardian</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="email@example.com"
                        className={`${inputClass("email")} pl-10`}
                      />
                    </div>
                    <ErrorMsg name="email" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="+977-9XXXXXXXXX"
                          className={`${inputClass("phone")} pl-10`}
                        />
                      </div>
                      <ErrorMsg name="phone" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alternate Phone
                      </label>
                      <input
                        name="alternatePhone"
                        value={form.alternatePhone}
                        onChange={handleChange}
                        placeholder="Optional"
                        className={inputClass("alternatePhone")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Home Address *
                    </label>
                    <textarea
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Street address"
                      className={`${inputClass("address")} resize-none`}
                    />
                    <ErrorMsg name="address" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="Kathmandu"
                        className={inputClass("city")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Occupation
                      </label>
                      <input
                        name="occupation"
                        value={form.occupation}
                        onChange={handleChange}
                        placeholder="Job/profession"
                        className={inputClass("occupation")}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-primary-500 font-serif mb-6">
                  Documents & Review
                </h2>

                {/* Review summary */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5 mb-6">
                  <h3 className="font-semibold text-primary-500 mb-4 text-sm">
                    Application Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <span className="text-gray-500">Student Name</span>
                    <span className="font-medium text-gray-800">
                      {form.studentName || "-"}
                    </span>
                    <span className="text-gray-500">Class Applying</span>
                    <span className="font-medium text-gray-800">
                      {form.classApplyingFor || "-"}
                    </span>
                    <span className="text-gray-500">Date of Birth</span>
                    <span className="font-medium text-gray-800">
                      {form.dateOfBirth || "-"}
                    </span>
                    <span className="text-gray-500">Parent Name</span>
                    <span className="font-medium text-gray-800">
                      {form.parentName || "-"}
                    </span>
                    <span className="text-gray-500">Contact Email</span>
                    <span className="font-medium text-gray-800">
                      {form.email || "-"}
                    </span>
                    <span className="text-gray-500">Phone</span>
                    <span className="font-medium text-gray-800">
                      {form.phone || "-"}
                    </span>
                  </div>
                </div>

                {/* Document Upload */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Documents (PDF/ZIP)
                  </label>
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors">
                    <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-gray-500 text-sm">
                      {form.documents
                        ? form.documents.name
                        : "Click to upload documents"}
                    </span>
                    <span className="text-gray-400 text-xs mt-1">
                      PDF, JPG, PNG up to 10MB
                    </span>
                    <input
                      type="file"
                      name="documents"
                      onChange={handleChange}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.zip"
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Any additional information you want to share..."
                    className={`${inputClass("notes")} resize-none`}
                  />
                </div>

                <div className="mt-5 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-700">
                  By submitting this form, you confirm that all information
                  provided is accurate and complete. Providing false information
                  will result in cancellation of admission.
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={prevStep}
              disabled={step === 0}
              className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiChevronLeft className="w-4 h-4" />
              Previous
            </button>

            {step < 2 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors"
              >
                Next Step
                <FiChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-secondary-500 text-white rounded-xl font-semibold hover:bg-secondary-600 transition-colors disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Submit Application
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionForm;
